import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Combobox, IComboboxOption } from '@/components/Combobox';
import {
    ENZRegion,
    EState,
    EStateOrRegion,
    IAddress,
    selectAddressByIndex,
    selectLicenseState,
    setAddressByIndex,
    setLicenseState,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'AddressState';

export function AddressState({ showErrors, baseId }: IProps): JSX.Element {
    const isNZ = useAppSelector(selectIsNZ);

    // Initial options - different for NZ
    const options: IComboboxOption[] = isNZ
        ? [
              { id: 1, value: ENZRegion.AUCKLAND },
              { id: 2, value: ENZRegion.WELLINGTON },
              { id: 3, value: ENZRegion.CANTERBURY },
              { id: 4, value: ENZRegion.WAIKATO },
              { id: 5, value: ENZRegion.BAY_OF_PLENTY },
              { id: 6, value: ENZRegion.OTAGO },
              { id: 7, value: ENZRegion.MANAWATU_WANGANUI },
              { id: 8, value: ENZRegion.HAWKES_BAY },
              { id: 9, value: ENZRegion.TARANAKI },
              { id: 10, value: ENZRegion.SOUTHLAND },
              { id: 11, value: ENZRegion.NELSON },
              { id: 12, value: ENZRegion.NORTHLAND },
              { id: 13, value: ENZRegion.GISBORNE },
              { id: 14, value: ENZRegion.MARLBOROUGH },
              { id: 15, value: ENZRegion.TASMAN },
              { id: 16, value: ENZRegion.WEST_COAST },
          ]
        : [
              { id: 1, value: EState.NSW },
              { id: 2, value: EState.VIC },
              { id: 3, value: EState.QLD },
              { id: 4, value: EState.ACT },
              { id: 5, value: EState.WA },
              { id: 6, value: EState.NT },
              { id: 7, value: EState.SA },
              { id: 8, value: EState.TAS },
          ];

    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const dispatch = useAppDispatch();
    const licenseState = useAppSelector(selectLicenseState);

    // *****State*****
    const [query, setQuery] = useState<undefined | string>(address.state ?? ''); // Keep track of search -> Initially set to currentState

    // Ensure AU default like NSW is not pre-selected when country is NZ
    useEffect(() => {
        if (!isNZ) return;
        if (
            address.state === EState.NSW ||
            address.state === EState.VIC ||
            address.state === EState.QLD ||
            address.state === EState.ACT ||
            address.state === EState.WA ||
            address.state === EState.NT ||
            address.state === EState.SA ||
            address.state === EState.TAS
        ) {
            const newAddress: IAddress = { ...address, state: undefined };
            dispatch(setAddressByIndex({ index: 0, address: newAddress }));
            setQuery('');
        }
    }, [isNZ, address.state, dispatch]);

    // *****Functions*****
    // Handle the onChange event for the input.
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        setQuery(value);
        if (typeof value === 'boolean') return;
        if (value?.length > 3) return;
    }

    // When the user selects an option
    function handleSelect(option: IComboboxOption): void {
        const newAddress: IAddress = {
            ...address,
            state: option.value as EStateOrRegion,
        };
        dispatch(setAddressByIndex({ index: 0, address: newAddress }));
    }

    // dispatch same state for license as well if license state not yet chosen
    if (!licenseState || licenseState === undefined) {
        dispatch(setLicenseState(address.state as EStateOrRegion));
    }
    // *****Render*****
    const heading = isNZ ? 'Region' : 'State Name';
    const placeholder = isNZ ? 'Region' : 'State';
    return (
        <div data-testid={testId}>
            <Combobox
                options={options}
                placeholder={placeholder}
                heading={heading}
                onChange={handleChange}
                query={query ?? ''}
                onSelect={handleSelect}
                showErrors={showErrors && !address.state}
                baseId={baseId}
            />
        </div>
    );
}
